import { useMemo, useState } from "react";
import type { Dispatch, SetStateAction } from "react";

import type { OperatorLogSink } from "../../types/log";
import { formatSystemLine, parseDcMessage } from "./format";
import type { ParsedDcMessage } from "./format";

const CAPS = { system: 5000, incoming: 80, outgoing: 80, bridge: 200 };

/** Periodic robot → UI streams (PROTOCOL.md §4): state, not events — kept out of the
 * Data bridge log, which shows operator commands and the backend's replies to them. */
const PERIODIC_EVENTS = new Set([
  "telemetry",
  "control-status",
  "link-quality",
  "joint-state",
  "camera-video-stats",
]);
const PERIODIC_OUTGOING_EVENTS = new Set(["monitor-heartbeat", "operator-input", "video-clock-ping"]);

function eventOf(raw: unknown): string | null {
  try {
    const parsed = JSON.parse(String(raw ?? "")) as Record<string, unknown>;
    const event = parsed?.event ?? parsed?.type;
    return typeof event === "string" ? event : null;
  } catch {
    return null;
  }
}

/** Direction marker on the tag: "[send-stop]" → "[→ send-stop]". */
function withDirection(entry: ParsedDcMessage, arrow: "→" | "←"): ParsedDcMessage {
  return { ...entry, tag: entry.tag.replace(/^\[/, `[${arrow} `) };
}

function push<T>(setter: Dispatch<SetStateAction<T[]>>, cap: number, next: T) {
  setter((prev) => {
    const arr = [...prev, next];
    return arr.length > cap ? arr.slice(arr.length - cap) : arr;
  });
}

export interface UseOperatorLogsResult {
  log: OperatorLogSink;
  system: string[];
  incoming: ParsedDcMessage[];
  outgoing: ParsedDcMessage[];
  /** Data bridge: operator commands (→, DataChannel `chat`) and the backend's
   * non-periodic replies (← robot-config, error, …), in arrival order. */
  bridge: ParsedDcMessage[];
}

/**
 * Owns the log buffers and exposes a stable `log` object.
 */
export function useOperatorLogs(): UseOperatorLogsResult {
  const [system, setSystem] = useState<string[]>([]);
  const [incoming, setIncoming] = useState<ParsedDcMessage[]>([]);
  const [outgoing, setOutgoing] = useState<ParsedDcMessage[]>([]);
  const [bridge, setBridge] = useState<ParsedDcMessage[]>([]);

  const log = useMemo<OperatorLogSink>(
    () => ({
      system: (text, meta = {}) => push(setSystem, CAPS.system, formatSystemLine(text, meta)),
      incoming: (message) => {
        // Clock probes are periodic internal diagnostics; rendering the ping/pong
        // history competes with the compositor being measured.
        const event = eventOf(message);
        if (event === "video-clock-pong" || event === "camera-video-stats") return;
        const entry = parseDcMessage(message);
        push(setIncoming, CAPS.incoming, entry);
        if (!event || !PERIODIC_EVENTS.has(event)) {
          push(setBridge, CAPS.bridge, withDirection(entry, "←"));
        }
      },
      outgoing: (text) => {
        // The monitor heartbeat runs at 10 Hz. Rendering every sample in two
        // bounded buffers competes with video decoding and keyboard streaming.
        const event = eventOf(text);
        if (event && PERIODIC_OUTGOING_EVENTS.has(event)) return;
        const entry = parseDcMessage(text);
        push(setOutgoing, CAPS.outgoing, entry);
        push(setBridge, CAPS.bridge, withDirection(entry, "→"));
      },
    }),
    []
  );

  return { log, system, incoming, outgoing, bridge };
}
